import React, { Component } from 'react';
import AuthService from '../../services/api/auth';
import LoadingModal from '../../components/LoadingModal';
import DriversLicenseCheckForm from '../../components/DriversLicenseCheckForm';
import { getFormValues, SubmissionError, getFormSyncErrors, isDirty } from 'redux-form';
import { connect } from 'react-redux';
import * as registerActions from './actions/';
import { bindActionCreators } from 'redux';
import { withAlert } from 'react-alert';
import Helper from '../../helpers';
import { dateIsValid, expirationDateIsValid, required, expirationDateIsValidOrEmpty } from '../../inputs/validations';

const states = AuthService.getStates();

class DriversLicenseCheckDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            errors: {},
            initialData: {},
            isExpirationDateHidden: false,
        };
    }

    componentDidMount() {
        /**
         * function to redirect back to the previous step
         */
        const imageUploadRedirect = () => {
            const newPath =
                (window.VULOG_CONFIG &&
                    window.VULOG_CONFIG.STEPS &&
                    window.VULOG_CONFIG.STEPS.find(s => s.title === 'DL') &&
                    window.VULOG_CONFIG.STEPS.find(s => s.title === 'DL').path) ||
                '/';
            return this.props.history.push(newPath);
        };

        /**
         * check if the form requires a single photo
         */
        if (window.VULOG_CONFIG.IS_DL_SINGLE_PHOTO) {
            if (!window.VULOG_DL_FRONT_IMAGE) {
                return imageUploadRedirect();
            }
        } else if (!window.VULOG_DL_FRONT_IMAGE || !window.VULOG_DL_BACK_IMAGE) {
            return imageUploadRedirect();
        }
        this.props.setStep('DL_CHECK');
        AuthService.getDriversLicenseDetails().then(response => {
            this.props.populateForm(response, 'license');
            this.setState({
                initialData: response,
                isExpirationDateHidden: window.VULOG_CONFIG.IS_DL_EXPIRY_DATE_HIDDEN,
                isExpirationDateMandatory: !window.VULOG_CONFIG.IS_DL_EXPIRATION_DATE_OPTIONAL,
            });
            this.setState({
                loading: false,
            });
        });
    }

    requestPaymentUrl() {
        return AuthService.requestPaymentUrl().then(response => {
            this.setState({
                loading: false,
            });
            Helper.post(response.url.url, response.url.params);
        });
    }

    onSubmit(initialData) {
        let validationMapping = {
            Country: [required],
            DocumentNumber: [required],
            GivenName: [required],
            Surname: [required],
            BirthDate: [dateIsValid, required],
        };

        if (this.state.isExpirationDateMandatory && !this.state.isExpirationDateHidden) {
            validationMapping['ExpirationDate'] = [expirationDateIsValid, required];
        }

        if (!this.state.isExpirationDateMandatory) {
            validationMapping['ExpirationDate'] = [expirationDateIsValidOrEmpty];
        }

        const { formValues } = this.props;
        let stateCode = null;
        if (formValues.IssuingStateCode) {
            if (states.find(state => state.value === formValues.IssuingStateCode)) {
                stateCode = formValues.IssuingStateCode;
            }
        }
        let data = {
            ...initialData,
            BirthDate: initialData.BirthDate ? Helper.formatDate(initialData.BirthDate) : null,
            IssuingStateCode: formValues.Country === 'USA' ? stateCode : formValues.IssuingStateCode,
        };

        if (data.ExpirationDate) {
            data.ExpirationDate = Helper.formatDate(initialData.ExpirationDate);
            if (data.ExpirationDate === 'Invalid date') {
                data.ExpirationDate = null;
            }
        }

        if (window.VULOG_CONFIG.IS_DL_EXPIRY_DATE_HIDDEN) {
            data.ExpirationDate = null;
        }

        if (window.VULOG_CONFIG.CHECKR_ENABLED && formValues.Country === 'USA') {
            validationMapping['IssuingStateCode'] = [required];
        }

        return new Promise((resolve, cancel) => {
            let errors = {};

            Object.keys(validationMapping).forEach(key => {
                validationMapping[key].forEach(func => {
                    const message = func(data[key], data);
                    if (message) {
                        errors[key] = message;
                    }
                });
            });
            resolve(errors);
        })
            .then(errors => {
                if (Object.keys(errors).length > 0) {
                    this.setState({
                        errors,
                    });
                    throw new SubmissionError(errors);
                }

                this.setState({
                    loading: true,
                });
            })
            .then(() => {
                const requestData = new FormData();

                if (window.VULOG_CONFIG.IS_DL_SINGLE_PHOTO) {
                    requestData.append('front', window.VULOG_DL_FRONT_IMAGE);
                } else {
                    requestData.append('front', window.VULOG_DL_FRONT_IMAGE);
                    requestData.append('back', window.VULOG_DL_BACK_IMAGE);
                }

                Object.keys(data).forEach(key => {
                    requestData.append(key, data[key]);
                });

                return AuthService.saveDriversLicenseDetails(requestData)
                    .then(response => {
                        AuthService.getAccountStatus().then(response => {
                            if (response.next === null) {
                                this.props.history.push('/register/status');
                            } else {
                                this.props.history.push(response.next);
                            }
                        });
                    })
                    .catch(error => {
                        this.setState({
                            loading: false,
                        });
                        if (error.data && error.data.message) {
                            throw new SubmissionError({ _error: error.data.message });
                        } else if (error.message) {
                            throw new SubmissionError({ _error: error.message });
                        } else {
                            throw new SubmissionError({ _error: 'DEFAULT_ERROR_MESSAGE' });
                        }
                    });
            });
    }

    cancel() {
        AuthService.getAccountStatus().then(response => {
            response.steps.forEach((entry, i) => {
                if (entry.path === this.props.location.pathname) {
                    if (entry.back) {
                        this.props.history.push(entry.back);
                    } else {
                        const lastScreen = response.steps[i - 1];
                        if (lastScreen && lastScreen.path) {
                            this.props.history.push(lastScreen.path);
                        }
                    }
                }
            });
        });
    }

    render() {
        return (
            <div className="container-fluid wrapper">
                <div className="d-flex flex-column full-height menu-bar-bottom">
                    <h3 className="page__title">{window.VULOG_TRANSLATE('DRIVERS LICENSE CHECK DETAILS')}</h3>

                    <p className="page__warning">{window.VULOG_TRANSLATE('DATA_CHANGE_WARNING_DL')}</p>

                    <div className="f-container flex-grow-1">
                        <DriversLicenseCheckForm
                            hideExpirationDate={this.state.isExpirationDateHidden}
                            errors={{ ...this.props.submitErrors, ...this.state.errors }}
                            ref={ref => {
                                this._form = ref;
                            }}
                            {...this.props}
                            onSubmit={this.onSubmit.bind(this)}
                        />
                    </div>

                    <div className="d-flex flex-row mt-auto mb-1-5rem">
                        <button className="btn btn-primary btn-block" onClick={() => this.cancel()}>
                            <i className="material-icons va-middle reg">keyboard_arrow_left</i>
                            {window.VULOG_TRANSLATE('Back')}
                        </button>
                        <button className="btn btn-primary btn-block" onClick={() => this._form.submit()}>
                            {window.VULOG_TRANSLATE('Next')}
                            <i className="material-icons va-middle reg">keyboard_arrow_right</i>
                        </button>
                    </div>
                    <LoadingModal loading={this.state.loading} />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        license: state.register.license,
        initialValues: state.register.license,
        enableReinitialize: true,
        formValues: getFormValues('drivers-license-check')(state),
        submitErrors: getFormSyncErrors('drivers-license-check')(state),
        isDirty: isDirty('drivers-license-check')(state),
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(registerActions, dispatch);
}

export default withAlert(connect(mapStateToProps, mapDispatchToProps)(DriversLicenseCheckDetails));
